import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  Injector,
  Input,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FusionState } from '../../../core/h3ml/state/fusion-state';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../core/h3ml/state/output-info';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../core/h3ml/state/task-workspace-store';
import { FusionManager } from '../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import {
  convertToPointCloudCoordinates,
  shouldRenderOrthographic,
} from '../../../core/outputs/annotation/annotation-shared/fusion/projection/orthographic-orchestrator';
import { InteractionModeService } from '../../../core/outputs/annotation/annotation-shared/utils/interaction-mode.service';
import { ZoomPanelService } from '../../../core/outputs/annotation/annotation-shared/zoom-panel/zoom-panel.service';
import { FeatureFlagService } from '../../../core/shared/misc-services/feature-flag.service';
import { getCalibrationData } from '../../../cross-team-shared/misc-utils/calibration-data';
import { getTotalFusedOutputs } from '../../../cross-team-shared/misc-utils/project-utils';
import { CalibrationData } from '../../../team-apis/otterdam/calibration-data';
import { VideoOutput } from '../../../team-apis/otterdam/outputs/video-output';
import { contentInjectorToken } from '../annotation-shared/abstract-annotation-output-component';
import { AnnotationState2D } from '../annotation-shared/state/root/annotation-state-2d';
import { ShapeFilterService } from '../annotation-shared/toolbars/shape-list-and-tag-editor/shape-filter/shape-filter.service';

@Component({
  selector: 'h3-video-layout',
  templateUrl: './video-layout.component.html',
  styleUrls: [
    '../annotation-shared/toolbars/toolbar-grid.scss',
    './video-layout.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VideoLayoutComponent extends DirectiveSuperclass {
  @Input({ required: true }) state!: AnnotationState2D;

  primaryOutputInjector?: Injector;
  calibrationData$?: Observable<CalibrationData | undefined>;
  interactionModeIsCanvas$ = this.interactionModeService.isInMode$('canvas');
  readOnly$ = this.taskWorkspaceStore('readOnly').$;
  outputName: string;
  showProjectionSettings$ = of(false);
  isMlExtrapolationEnabled =
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    this.outputInfoStore.state().output.meta.ml_extrapolation_options?.enabled;
  isThumbnail$: Observable<boolean>;
  isOrthographicProjection = false;
  showSyncSubpointShapesButton =
    this.featureFlagService.hasFlag('subpoint-tags');
  isRightPanelExpanded$ = this.fusionStore('isRightPanelExpanded').$;
  areAllInFusionSingleFrame$ = this.fusionManager.areAllSingleFrame$;
  convertToPointCloudCoordinates = this.getConvertToPointCloudCoordinates();
  isSplitViewEnabled = this.buildIsSplitViewEnabled();
  isInSplitView$ = this.taskWorkspaceStore('splitViewOutputName').$.pipe(
    map(isDefined),
  );

  isSelectedOutput$ = this.taskWorkspaceStore('selectedOutputName').$.pipe(
    map(
      (selectedOutputName) =>
        selectedOutputName === this.outputInfoStore.state().output.name,
    ),
  );
  shouldShowCalibrationSettings = this.featureFlagService.hasFlag(
    'camera-calibration-debug_on',
  );

  protected isObjectsEnabled = this.taskWorkspaceStore.isUsingObjects();
  protected contentInjector = inject(contentInjectorToken);

  constructor(
    private featureFlagService: FeatureFlagService,
    private fusionManager: FusionManager,
    @Inject(fusionStoreToken) private fusionStore: Store<FusionState>,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo<VideoOutput>>,
    private interactionModeService: InteractionModeService,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private zoomPanelService: ZoomPanelService,
    injector: Injector,
    _shapeFilterService: ShapeFilterService,
  ) {
    super(injector);
    const output = outputInfoStore.state().output;
    this.outputName = output.name;
    this.isThumbnail$ = this.taskWorkspaceStore.isThumbnail$(output.name);
    this.isOrthographicProjection = output.meta.is_orthographic_projection;
    this.setUpSubscriptions();
    this.setUpProjectionSubscriptions();
  }

  private getConvertToPointCloudCoordinates() {
    return shouldRenderOrthographic(
      this.fusionManager.getPrimaryMinion(),
      this.outputInfoStore.state().output,
    )
      ? convertToPointCloudCoordinates
      : undefined;
  }

  private setUpProjectionSubscriptions() {
    const primaryMinion = this.fusionManager.getPrimaryMinion();
    if (primaryMinion.output.type !== 'PointCloudOutput') {
      return;
    }

    this.primaryOutputInjector = primaryMinion.injector;

    const outputId = this.outputInfoStore.state().output.id;
    this.calibrationData$ = combineLatest([
      this.taskWorkspaceStore('task').$,
      this.taskWorkspaceStore('project')('inputs').$,
    ]).pipe(
      map(([task, inputs]) => getCalibrationData(task, inputs, outputId)),
    );

    this.showProjectionSettings$ = combineLatest([
      this.calibrationData$,
      this.outputInfoStore('output')('meta')('is_orthographic_projection').$,
    ]).pipe(
      map(
        ([calibrationData, useOrthographic]) =>
          !!calibrationData && !useOrthographic,
      ),
    );
  }

  private setUpSubscriptions() {
    if (!this.isOrthographicProjection) {
      this.zoomPanelService.setUpZoomPanelSubscription({
        primaryOutputInjector: this.primaryOutputInjector,
        calibrationData$: this.calibrationData$,
      });
    }
  }

  private buildIsSplitViewEnabled() {
    const { project } = this.taskWorkspaceStore.state();
    return getTotalFusedOutputs(project) > 1;
  }
}
